import { useState, useEffect } from "react";
import { api } from "@/App";
import { 
  Settings, Building, Clock, MessageSquare, Save, 
  Send, Bell, Calendar, Users, Phone, MapPin,
  RefreshCw, CheckCircle, AlertCircle, Plus, Trash2,
  Wrench, FileText, GripVertical, Edit, User, Calculator
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChecklistTemplateConfig from "./ChecklistTemplateConfig";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

// Sub-components for each tab
import UsersManagementTab from "./settings/UsersManagementTab";
import WorkflowTab from "./settings/WorkflowTab";
import PrestationsTab from "./settings/PrestationsTab";
import SecurityTab from "./settings/SecurityTab";
import ClosedDaysTab from "./settings/ClosedDaysTab";

const JOURS_SEMAINE = [
  { value: "lundi", label: "Lundi" },
  { value: "mardi", label: "Mardi" },
  { value: "mercredi", label: "Mercredi" },
  { value: "jeudi", label: "Jeudi" },
  { value: "vendredi", label: "Vendredi" },
  { value: "samedi", label: "Samedi" },
  { value: "dimanche", label: "Dimanche" },
];

const SMS_VARIABLES = [
  { var: "{prenom}", desc: "Prénom du client" },
  { var: "{nom}", desc: "Nom du client" },
  { var: "{marque}", desc: "Marque du véhicule" },
  { var: "{modele}", desc: "Modèle du véhicule" },
  { var: "{immatriculation}", desc: "Plaque d'immatriculation" },
  { var: "{date}", desc: "Date du RDV" },
  { var: "{etape}", desc: "Étape du workflow (pour avancement)" },
  { var: "{link}", desc: "Lien du devis (modèle Devis)" },
  { var: "{montant}", desc: "Montant du devis (modèle Devis)" },
  { var: "{entreprise}", desc: "Nom de l'expéditeur SMS" },
];

export default function SettingsView() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("entreprise");
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchCredits();
  }, []);

  const fetchCredits = async () => {
    try {
      const response = await api.get("/sms/credits");
      setCredits(response.data);
    } catch (error) {
      setCredits(null);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get("/settings");
      setSettings(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    }
    setLoading(false);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put("/settings", settings);
      setSettings(response.data);
      toast.success("Paramètres enregistrés");
      setHasChanges(false);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  };

  const handleTestSms = async () => {
    if (!testPhone.trim()) {
      toast.error("Saisissez un numéro de téléphone pour le test");
      return;
    }
    if (hasChanges) {
      toast.error("Enregistrez d'abord vos modifications avant de tester");
      return;
    }
    setTesting(true);
    try {
      const response = await api.post("/sms/test", { telephone: testPhone });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Erreur lors du test SMS");
    }
    setTesting(false);
  };

  const toggleJour = (jour) => {
    const current = settings.planning_jours_ouverture || [];
    if (current.includes(jour)) {
      updateSetting("planning_jours_ouverture", current.filter(j => j !== jour));
    } else {
      updateSetting("planning_jours_ouverture", [...current, jour]);
    }
  };

  if (loading || !settings) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="settings-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Settings className="h-8 w-8" />
            Paramètres
          </h1>
          <p className="text-slate-600 mt-1">Configuration générale de l'application</p>
        </div>
        {(activeTab === "entreprise" || activeTab === "planning" || activeTab === "sms") && (
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Badge className="bg-amber-100 text-amber-700">
                <AlertCircle className="h-4 w-4 mr-1" />
                Non enregistré
              </Badge>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-settings-btn"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="entreprise" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Entreprise
          </TabsTrigger>
          <TabsTrigger value="planning" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Planning
          </TabsTrigger>
          <TabsTrigger value="sms" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="equipe" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Équipe
          </TabsTrigger>
          <TabsTrigger value="workflow" className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Workflow
          </TabsTrigger>
          <TabsTrigger value="prestations" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Prestations
          </TabsTrigger>
          <TabsTrigger value="checklist" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Check-list
          </TabsTrigger>
          <TabsTrigger value="securite" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Sécurité
          </TabsTrigger>
          <TabsTrigger value="fermetures" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Fermetures
          </TabsTrigger>
        </TabsList>

        {/* ENTREPRISE */}
        <TabsContent value="entreprise">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Informations de l'entreprise
              </CardTitle>
              <CardDescription>
                Ces informations apparaissent dans l'application et les communications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nom de l'entreprise</Label>
                  <Input
                    value={settings.entreprise_nom || ""}
                    onChange={(e) => updateSetting("entreprise_nom", e.target.value)}
                    placeholder="Carrosserie Auto Gardoise"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={settings.entreprise_telephone || ""}
                      onChange={(e) => updateSetting("entreprise_telephone", e.target.value)}
                      placeholder="04 66 XX XX XX"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Textarea
                    value={settings.entreprise_adresse || ""}
                    onChange={(e) => updateSetting("entreprise_adresse", e.target.value)}
                    placeholder="Adresse complète..."
                    className="pl-10 min-h-[80px]"
                  />
                </div>
              </div>
              
              <Separator />
              
              {/* Charges mensuelles pour rentabilité */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-slate-600" />
                  <Label className="text-lg font-semibold">Rentabilité</Label>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="space-y-2">
                    <Label>Charges fixes mensuelles (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.charges_mensuelles || ""}
                      onChange={(e) => updateSetting("charges_mensuelles", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="max-w-xs"
                    />
                    <p className="text-xs text-slate-500">
                      Loyer, salaires, charges fixes... Ce montant sera réparti sur les semaines pour le calcul de rentabilité.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANNING */}
        <TabsContent value="planning">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horaires et capacité
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Heures de travail / jour</Label>
                  <Input
                    type="number"
                    min={1}
                    max={24}
                    value={settings.heures_travail_jour || 8}
                    onChange={(e) => updateSetting("heures_travail_jour", parseInt(e.target.value))}
                  />
                  <p className="text-xs text-slate-500">Pour le calcul de charge</p>
                </div>
                <div className="space-y-2">
                  <Label>Capacité véhicules / jour</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={settings.capacite_vehicules_jour || 5}
                    onChange={(e) => updateSetting("capacite_vehicules_jour", parseInt(e.target.value))}
                  />
                  <p className="text-xs text-slate-500">Nombre max de véhicules simultanés</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label>Horaires d'ouverture</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Heure d'ouverture</Label>
                    <Input
                      type="time"
                      value={settings.planning_heure_debut || "08:00"}
                      onChange={(e) => updateSetting("planning_heure_debut", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Heure de fermeture</Label>
                    <Input
                      type="time"
                      value={settings.planning_heure_fin || "18:00"}
                      onChange={(e) => updateSetting("planning_heure_fin", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label>Jours d'ouverture</Label>
                <div className="flex flex-wrap gap-3">
                  {JOURS_SEMAINE.map((jour) => {
                    const isSelected = (settings.planning_jours_ouverture || []).includes(jour.value);
                    return (
                      <div
                        key={jour.value}
                        onClick={() => toggleJour(jour.value)}
                        className={`px-4 py-2 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? "bg-red-50 border-red-300 text-red-700"
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={isSelected} />
                          <span className="font-medium">{jour.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS */}
        <TabsContent value="sms">
          <div className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Configuration SMS (AllMySMS)
                </CardTitle>
                <CardDescription>
                  Identifiants de connexion à l'API AllMySMS. Disponibles dans votre compte AllMySMS → Paramètres → API.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${settings.sms_enabled ? "bg-emerald-100" : "bg-red-100"}`}>
                      {settings.sms_enabled ? (
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold">Service SMS</p>
                      <p className="text-sm text-slate-500">{settings.sms_enabled ? "Activé" : "Désactivé"}</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.sms_enabled}
                    onCheckedChange={(v) => updateSetting("sms_enabled", v)}
                    data-testid="sms-enabled-switch"
                  />
                </div>

                <Separator />

                {/* Identifiants API */}
                {/* Solde SMS */}
                {credits?.success && (
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    (credits.nb_sms ?? 0) < 50 ? "bg-red-50 border border-red-200" : "bg-emerald-50 border border-emerald-200"
                  }`} data-testid="sms-credits-panel">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${(credits.nb_sms ?? 0) < 50 ? "bg-red-100" : "bg-emerald-100"}`}>
                        <MessageSquare className={`h-5 w-5 ${(credits.nb_sms ?? 0) < 50 ? "text-red-600" : "text-emerald-600"}`} />
                      </div>
                      <div>
                        <p className="font-semibold">Solde du compte AllMySMS</p>
                        <p className="text-sm text-slate-600">
                          <span className="font-bold">{credits.balance} €</span> · environ <span className="font-bold">{credits.nb_sms} SMS</span> restants
                        </p>
                        {(credits.nb_sms ?? 0) < 50 && (
                          <p className="text-xs text-red-600 font-medium mt-1">
                            ⚠ Solde faible — pensez à recharger votre compte AllMySMS
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={fetchCredits} title="Actualiser le solde" data-testid="sms-credits-refresh-btn">
                      <RefreshCw className="h-4 w-4 text-slate-500" />
                    </Button>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Identifiants API</Label>
                    {settings.sms_api_configured ? (
                      <Badge className="bg-emerald-100 text-emerald-700" data-testid="sms-api-status-badge">
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                        Configuré
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700" data-testid="sms-api-status-badge">
                        <AlertCircle className="h-3.5 w-3.5 mr-1" />
                        Non configuré
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Login AllMySMS</Label>
                      <Input
                        value={settings.sms_api_login || ""}
                        onChange={(e) => updateSetting("sms_api_login", e.target.value)}
                        placeholder="votre-login"
                        data-testid="sms-api-login-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Clé API</Label>
                      <Input
                        type="password"
                        value={settings.sms_api_key || ""}
                        onChange={(e) => updateSetting("sms_api_key", e.target.value)}
                        placeholder={settings.sms_api_key_masked || "Saisir la clé API"}
                        data-testid="sms-api-key-input"
                      />
                      {settings.sms_api_key_masked && (
                        <p className="text-xs text-slate-500">
                          Clé actuelle : {settings.sms_api_key_masked} — laisser vide pour conserver
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Expéditeur</Label>
                      <Input
                        value={settings.sms_api_sender || ""}
                        onChange={(e) => updateSetting("sms_api_sender", e.target.value)}
                        placeholder="GARAGE"
                        maxLength={11}
                        data-testid="sms-api-sender-input"
                      />
                      <p className="text-xs text-slate-500">Nom affiché sur le SMS (max 11 caractères)</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Test de configuration */}
                <div className="space-y-3 p-4 border border-blue-200 bg-blue-50 rounded-lg">
                  <Label className="flex items-center gap-2 font-semibold">
                    <Send className="h-4 w-4 text-blue-600" />
                    Tester la configuration
                  </Label>
                  <p className="text-sm text-slate-600">
                    Envoie un SMS de test au numéro indiqué pour vérifier que la configuration fonctionne.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Input
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="06 XX XX XX XX"
                      className="max-w-xs bg-white"
                      data-testid="sms-test-phone-input"
                    />
                    <Button
                      onClick={handleTestSms}
                      disabled={testing}
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="sms-test-send-btn"
                    >
                      {testing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Envoyer le SMS de test
                    </Button>
                  </div>
                  {hasChanges && (
                    <p className="text-xs text-amber-600 font-medium">
                      ⚠ Enregistrez d'abord vos modifications avant de lancer le test
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Envois automatiques
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                  <div>
                    <p className="font-medium">Confirmation de RDV</p>
                    <p className="text-sm text-slate-500">SMS envoyé à la création d'un RDV</p>
                  </div>
                  <Switch
                    checked={settings.sms_auto_confirmation}
                    onCheckedChange={(v) => updateSetting("sms_auto_confirmation", v)}
                    disabled={!settings.sms_enabled}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="font-medium">Rappel avant RDV</p>
                    <p className="text-sm text-slate-500">SMS de rappel envoyé avant le rendez-vous</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={72}
                        value={settings.sms_rappel_heures_avant || 24}
                        onChange={(e) => updateSetting("sms_rappel_heures_avant", parseInt(e.target.value))}
                        className="w-20"
                        disabled={!settings.sms_enabled || !settings.sms_auto_rappel}
                      />
                      <span className="text-sm text-slate-500">h avant</span>
                    </div>
                    <Switch
                      checked={settings.sms_auto_rappel}
                      onCheckedChange={(v) => updateSetting("sms_auto_rappel", v)}
                      disabled={!settings.sms_enabled}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                  <div>
                    <p className="font-medium">Véhicule terminé</p>
                    <p className="text-sm text-slate-500">SMS envoyé quand le véhicule passe en "Terminé"</p>
                  </div>
                  <Switch
                    checked={settings.sms_auto_termine}
                    onCheckedChange={(v) => updateSetting("sms_auto_termine", v)}
                    disabled={!settings.sms_enabled}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Notifications SMS pour l'admin */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-purple-600" />
                  Notifications Admin (SMS)
                </CardTitle>
                <CardDescription>
                  Recevez un SMS quand un collaborateur ajoute un commentaire ou une photo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${settings.notifications_sms_enabled ? "bg-purple-100" : "bg-slate-100"}`}>
                      {settings.notifications_sms_enabled ? (
                        <Bell className="h-5 w-5 text-purple-600" />
                      ) : (
                        <Bell className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold">Notifications SMS</p>
                      <p className="text-sm text-slate-500">
                        {settings.notifications_sms_enabled 
                          ? "Vous recevrez un SMS pour chaque commentaire/photo" 
                          : "SMS désactivés, notifications in-app uniquement"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.notifications_sms_enabled}
                    onCheckedChange={(v) => updateSetting("notifications_sms_enabled", v)}
                    disabled={!settings.sms_enabled}
                  />
                </div>

                {settings.notifications_sms_enabled && (
                  <div className="space-y-2 p-4 border border-purple-200 rounded-lg">
                    <Label className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-purple-600" />
                      Votre numéro de téléphone
                    </Label>
                    <Input
                      value={settings.admin_phone_number || ""}
                      onChange={(e) => updateSetting("admin_phone_number", e.target.value)}
                      placeholder="06 XX XX XX XX"
                      className="max-w-xs"
                    />
                    <p className="text-xs text-slate-500">
                      Ce numéro recevra les notifications pour les commentaires et photos des collaborateurs
                    </p>
                  </div>
                )}

                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Note:</span> Les notifications in-app (cloche) sont toujours actives. 
                    Les SMS sont envoyés en plus pour les événements prioritaires (commentaires/photos).
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Modèles de SMS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-800 mb-2">Variables disponibles :</p>
                  <div className="flex flex-wrap gap-2">
                    {SMS_VARIABLES.map((v) => (
                      <Badge key={v.var} variant="outline" className="text-blue-700 border-blue-300" title={v.desc}>
                        {v.var}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700">Confirmation</Badge>
                  </Label>
                  <Textarea
                    value={settings.sms_template_confirmation || ""}
                    onChange={(e) => updateSetting("sms_template_confirmation", e.target.value)}
                    placeholder="Bonjour {prenom}, votre RDV est confirmé..."
                    className="min-h-[80px] font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">{(settings.sms_template_confirmation || "").length}/160</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-700">Rappel</Badge>
                  </Label>
                  <Textarea
                    value={settings.sms_template_rappel || ""}
                    onChange={(e) => updateSetting("sms_template_rappel", e.target.value)}
                    placeholder="Rappel: RDV demain..."
                    className="min-h-[80px] font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">{(settings.sms_template_rappel || "").length}/160</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">Avancement</Badge>
                  </Label>
                  <Textarea
                    value={settings.sms_template_avancement || ""}
                    onChange={(e) => updateSetting("sms_template_avancement", e.target.value)}
                    placeholder="Info: Votre véhicule est en {etape}..."
                    className="min-h-[80px] font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">{(settings.sms_template_avancement || "").length}/160</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-700">Terminé</Badge>
                  </Label>
                  <Textarea
                    value={settings.sms_template_termine || ""}
                    onChange={(e) => updateSetting("sms_template_termine", e.target.value)}
                    placeholder="Bonne nouvelle! Votre véhicule est prêt..."
                    className="min-h-[80px] font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">{(settings.sms_template_termine || "").length}/160</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-700">Devis</Badge>
                  </Label>
                  <Textarea
                    value={settings.sms_template_devis || ""}
                    onChange={(e) => updateSetting("sms_template_devis", e.target.value)}
                    placeholder="Bonjour {prenom}, voici votre devis {montant}: {link}. {entreprise}"
                    className="min-h-[80px] font-mono text-sm"
                    data-testid="sms-template-devis"
                  />
                  <p className="text-xs text-slate-500">Utilisez {"{link}"} et {"{montant}"} — {(settings.sms_template_devis || "").length}/160</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* EQUIPE */}
        <TabsContent value="equipe">
          <UsersManagementTab />
        </TabsContent>

        {/* WORKFLOW */}
        <TabsContent value="workflow">
          <WorkflowTab />
        </TabsContent>

        {/* PRESTATIONS */}
        <TabsContent value="prestations">
          <PrestationsTab />
        </TabsContent>

        {/* CHECK-LIST */}
        <TabsContent value="checklist">
          <ChecklistTemplateConfig />
        </TabsContent>

        {/* SECURITE */}
        <TabsContent value="securite">
          <SecurityTab />
        </TabsContent>

        {/* FERMETURES */}
        <TabsContent value="fermetures">
          <ClosedDaysTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
